import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

type ToastKind = 'info' | 'error';
interface ToastEntry {
  id: number;
  kind: ToastKind;
  message: string;
}

interface ToastCtx {
  show: (message: string, kind?: ToastKind) => void;
}

const Ctx = createContext<ToastCtx | null>(null);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<ToastEntry[]>([]);

  const show = useCallback((message: string, kind: ToastKind = 'info') => {
    const id = Date.now() + Math.random();
    setItems((xs) => [...xs, { id, kind, message }]);
    setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== id)), 4500);
  }, []);

  const ctx = useMemo(() => ({ show }), [show]);

  return (
    <Ctx.Provider value={ctx}>
      {children}
      <div aria-live="polite" aria-atomic="true" className="fixed bottom-6 right-6 z-50 flex flex-col gap-2">
        {items.map((t) => (
          <div
            key={t.id}
            role="status"
            className={`rounded-lg px-4 py-3 shadow-lg border ${
              t.kind === 'error'
                ? 'bg-danger-bg text-danger border-danger/30'
                : 'bg-bg-card border-border-default'
            }`}
          >
            {t.message}
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}

export function useToast(): ToastCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error('useToast outside ToastProvider');
  return c;
}

export function useAuthErrorToast(onRouteHome: () => void) {
  const { show } = useToast();
  useEffect(() => {
    import('../lib/api').then(({ setAuthErrorHandler }) => {
      setAuthErrorHandler((kind) => {
        if (kind === 'expired') show('Tu sesión expiró', 'error');
        else if (kind === 'revoked') show('Tu sesión fue revocada. Contacta al administrador.', 'error');
        onRouteHome();
      });
    });
  }, [show, onRouteHome]);
}
